import {
  createStore,
  get,
  set,
  del,
  clear,
  keys,
  entries,
  type UseStore,
} from "idb-keyval";
import { plainify } from "~/utils/plainify";

// Lightweight per-DB key-value bucket. Used by runtime stores (jobs,
// receipts, tableStates, soldout+splitBill) to mirror Pinia state into
// IndexedDB so the app can boot offline.
//
// Each bucket is its own IDB database (DB name = bucket name) with a single
// object store called `kv`. No envelope, no version tracking — these
// resources are mirrors of realtime/transient state.

const bucketCache = new Map<string, UseStore>();

function getStore(name: string): UseStore {
  let s = bucketCache.get(name);
  if (!s) {
    s = createStore(name, "kv");
    bucketCache.set(name, s);
  }
  return s;
}

export type Bucket = {
  get: <T = unknown>(key: IDBValidKey) => Promise<T | undefined>;
  set: <T = unknown>(key: IDBValidKey, value: T) => Promise<void>;
  del: (key: IDBValidKey) => Promise<void>;
  clear: () => Promise<void>;
  keys: <K extends IDBValidKey = IDBValidKey>() => Promise<K[]>;
  entries: <T = unknown>() => Promise<[IDBValidKey, T][]>;
};

export function bucket(name: string): Bucket {
  const store = getStore(name);
  return {
    get: <T = unknown>(key: IDBValidKey) =>
      get(key, store).catch((err) => {
        console.warn(`[bucket:${name}] get(${String(key)}) failed`, err);
        return undefined;
      }) as Promise<T | undefined>,
    set: <T = unknown>(key: IDBValidKey, value: T) =>
      set(key, plainify(value), store).catch((err) => {
        console.warn(`[bucket:${name}] set(${String(key)}) failed`, err);
      }),
    del: (key: IDBValidKey) =>
      del(key, store).catch((err) => {
        console.warn(`[bucket:${name}] del(${String(key)}) failed`, err);
      }),
    clear: () =>
      clear(store).catch((err) => {
        console.warn(`[bucket:${name}] clear failed`, err);
      }),
    keys: <K extends IDBValidKey = IDBValidKey>() =>
      keys<K>(store).catch((err) => {
        console.warn(`[bucket:${name}] keys failed`, err);
        return [] as K[];
      }),
    entries: <T = unknown>() =>
      entries(store).catch((err) => {
        console.warn(`[bucket:${name}] entries failed`, err);
        return [] as [IDBValidKey, T][];
      }) as Promise<[IDBValidKey, T][]>,
  };
}

// ── Boot-time offline detection ───────────────────────────────────────
// Rule: `navigator.onLine === false` → offline; anything else → online.
// Result is cached for the page lifetime so every reader gets the same
// answer. Captive-portal false-positives are handled downstream by
// versionSync's per-resource refetch fallback.

let _offlineBootCached: boolean | null = null;

/**
 * Decide whether bootstrap should take the offline path. Cached once per
 * boot. `timeoutMs` accepted for backwards compatibility, ignored.
 */
export async function detectOfflineBoot(_timeoutMs = 3000): Promise<boolean> {
  if (_offlineBootCached !== null) return _offlineBootCached;

  const offline =
    typeof navigator !== "undefined" && navigator.onLine === false;
  _offlineBootCached = offline;
  console.log(
    `[detectOfflineBoot] offline=${offline} (navigator.onLine=${
      typeof navigator !== "undefined" ? navigator.onLine : "n/a"
    })`
  );
  return offline;
}

/**
 * Synchronous read of the cached offline-boot status. `detectOfflineBoot`
 * must have completed before this is consulted (bootstrap orchestrates).
 * Falls back to `navigator.onLine` if detection hasn't run yet — unreliable
 * but better than nothing for callers that race the detection.
 */
export function isOfflineBoot(): boolean {
  if (_offlineBootCached !== null) return _offlineBootCached;
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

// ── Conventional bucket names per resource. Keep here so the close-shop
// cleanup can find them all in one place. ─────────────────────────────
export const offlineBucketNames = {
  jobs: (storeId: string) => `pos_jobs_${storeId}`,
  receipts: (storeId: string) => `pos_receipts_${storeId}`,
  tableStates: (storeId: string) => `pos_tablestates_${storeId}`,
  runtime: (storeId: string) => `pos_runtime_${storeId}`,
};

/**
 * Wipe every offline runtime bucket for the given store. Called from the
 * close-shop flow so the next business day starts with a clean slate.
 * Does not touch the version-tracked `general_{storeId}` cache (menus/
 * settings/etc) — those survive day rollovers.
 */
export async function clearOfflineRuntime(storeId: string): Promise<void> {
  if (!storeId) return;
  const names = [
    offlineBucketNames.jobs(storeId),
    offlineBucketNames.receipts(storeId),
    offlineBucketNames.tableStates(storeId),
    offlineBucketNames.runtime(storeId),
  ];
  await Promise.all(names.map((n) => bucket(n).clear()));
}
